import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { User } from '@verantos-dev/core-annotation-tool';

import { AddPersonMenu } from './AddPersonMenu';
import { RemovableAnnotatorTag } from './RemovableAnnotatorTag';
import { useEligiblePeople } from './useEligiblePeople';

interface EditablePeopleCardProps {
  title: string;
  addLabel: string;
  persisted: User[];
  added: User[];
  options: User[];
  optionsLoading: boolean;
  onAdd: (person: User) => void;
  onRemove: (id: string) => void;
  hint?: string;
  /** When true, persisted entries also show a remove cross. */
  removePersistedEnabled?: boolean;
  /** Minimum number of people that must remain; removal is disabled when at the limit. */
  minRequired?: number;
  /** Tooltip shown on the disabled cross when at the minimum. */
  minRequiredTooltip?: string;
}

const EditablePeopleCard = ({
  title,
  addLabel,
  persisted,
  added,
  options,
  optionsLoading,
  onAdd,
  onRemove,
  hint,
  removePersistedEnabled,
  minRequired = 0,
  minRequiredTooltip,
}: EditablePeopleCardProps) => {
  const theme = useTheme();
  const isAtMin = persisted.length + added.length <= minRequired;

  return (
    <Card sx={{ flex: 1 }} elevation={2}>
      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: theme.spacing(3) }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="h6">{persisted.length + added.length}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          {persisted.map((person) =>
            removePersistedEnabled ? (
              <RemovableAnnotatorTag
                key={person.id}
                name={person.label}
                onRemove={isAtMin ? undefined : () => onRemove(person.id)}
                disabled={isAtMin}
                disabledTooltip={isAtMin ? minRequiredTooltip : undefined}
              />
            ) : (
              <RemovableAnnotatorTag key={person.id} name={person.label} />
            )
          )}
          {added.map((person) => (
            <RemovableAnnotatorTag
              key={person.id}
              name={person.label}
              onRemove={isAtMin ? undefined : () => onRemove(person.id)}
              disabled={isAtMin}
              disabledTooltip={isAtMin ? minRequiredTooltip : undefined}
            />
          ))}
          <AddPersonMenu
            label={addLabel}
            options={options}
            onSelect={onAdd}
            loading={optionsLoading}
          />
        </Stack>
        {hint && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: theme.palette.text.secondary,
                mt: '2px',
                flexShrink: 0,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                fontSize: 13,
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {hint}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export interface PeopleSectionEditableProps {
  persistedAnnotators: User[];
  persistedSupervisors: User[];
  addedAnnotators: User[];
  addedSupervisors: User[];
  onAddAnnotator: (person: User) => void;
  onRemoveAnnotator: (id: string) => void;
  onAddSupervisor: (person: User) => void;
  onRemoveSupervisor: (id: string) => void;
}

export const PeopleSectionEditable = ({
  persistedAnnotators,
  persistedSupervisors,
  addedAnnotators,
  addedSupervisors,
  onAddAnnotator,
  onRemoveAnnotator,
  onAddSupervisor,
  onRemoveSupervisor,
}: PeopleSectionEditableProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const excludedAnnotatorIds = useMemo(
    () => new Set([...persistedAnnotators, ...addedAnnotators].map((p) => p.id)),
    [persistedAnnotators, addedAnnotators]
  );
  const excludedSupervisorIds = useMemo(
    () => new Set([...persistedSupervisors, ...addedSupervisors].map((p) => p.id)),
    [persistedSupervisors, addedSupervisors]
  );

  const { eligibleAnnotators, eligibleSupervisors, isAnnotatorsLoading, isSupervisorsLoading } =
    useEligiblePeople({
      excludedAnnotatorIds,
      excludedSupervisorIds,
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Typography variant="h6">
        {formatMessage({ id: 'admin-common.peopleSection.title' })}
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.spacing(6), width: '100%' }}>
        <EditablePeopleCard
          title={formatMessage({ id: 'admin-common.peopleSection.annotators' })}
          addLabel={formatMessage({ id: 'admin-common.peopleSection.addAnnotator' })}
          persisted={persistedAnnotators}
          added={addedAnnotators}
          options={eligibleAnnotators}
          optionsLoading={isAnnotatorsLoading}
          onAdd={onAddAnnotator}
          onRemove={onRemoveAnnotator}
          hint={formatMessage({ id: 'admin-common.peopleSection.annotatorRemovalHint' })}
        />
        <EditablePeopleCard
          title={formatMessage({ id: 'admin-common.peopleSection.supervisors' })}
          addLabel={formatMessage({ id: 'admin-common.peopleSection.addSupervisor' })}
          persisted={persistedSupervisors}
          added={addedSupervisors}
          options={eligibleSupervisors}
          optionsLoading={isSupervisorsLoading}
          onAdd={onAddSupervisor}
          onRemove={onRemoveSupervisor}
          removePersistedEnabled
          minRequired={1}
          minRequiredTooltip={formatMessage({
            id: 'admin-common.peopleSection.lastSupervisorTooltip',
          })}
          hint={formatMessage({ id: 'admin-common.peopleSection.supervisorRemovalHint' })}
        />
      </Box>
    </Box>
  );
};
